import { useEffect, useState } from "react";
import {
  useGetSettingsQuery,
  usePatchSettingsMutation,
  useReloadSettingsMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { toast } from "sonner";
import { RefreshCw, Save } from "lucide-react";
import { useAppSelector } from "@/store";
import UserSettings from "@/routes/UserSettings";

type Patch = Record<string, unknown>;

export default function Settings() {
  const role = useAppSelector((s) => s.auth.user?.role);
  if (role !== "admin") {
    return <UserSettings />;
  }
  return <AdminSettings />;
}

function AdminSettings() {
  const { data, isLoading, refetch } = useGetSettingsQuery();
  const [patchMutation, { isLoading: saving }] = usePatchSettingsMutation();
  const [reloadMutation, { isLoading: reloading }] = useReloadSettingsMutation();

  const [draft, setDraft] = useState<Patch>({});

  // Reset draft whenever fresh data lands
  useEffect(() => {
    if (data) setDraft({});
  }, [data]);

  const get = <T,>(key: string, fallback: T): T => {
    if (key in draft) return draft[key] as T;
    return ((data?.[key] as T) ?? fallback) as T;
  };
  const setField = (key: string, value: unknown) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const numberField = (key: string, fallback = 0, step = 1) => (
    <Input
      type="number"
      step={step}
      value={String(get<number>(key, fallback))}
      onChange={(e) => setField(key, parseFloat(e.target.value || "0"))}
    />
  );
  const intField = (key: string, fallback = 0) => (
    <Input
      type="number"
      step={1}
      value={String(get<number>(key, fallback))}
      onChange={(e) => setField(key, parseInt(e.target.value || "0", 10))}
    />
  );
  const boolField = (key: string, fallback = false) => (
    <input
      type="checkbox"
      className="h-4 w-4 accent-accent"
      checked={get<boolean>(key, fallback)}
      onChange={(e) => setField(key, e.target.checked)}
    />
  );
  const selectField = (key: string, options: string[], fallback: string) => (
    <select
      className="h-9 rounded-md border border-border bg-bg px-3 text-sm"
      value={get<string>(key, fallback)}
      onChange={(e) => setField(key, e.target.value)}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );

  const onSave = async () => {
    if (Object.keys(draft).length === 0) {
      toast.info("No changes");
      return;
    }
    if (!confirm("Save settings? Some changes apply to the next cycle/tick immediately.")) return;
    try {
      await patchMutation(draft).unwrap();
      toast.success("Settings saved");
      setDraft({});
      refetch();
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Save failed";
      toast.error(msg);
    }
  };

  const onReload = async () => {
    try {
      await reloadMutation().unwrap();
      toast.success("Settings reloaded from database");
      setDraft({});
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Reload failed";
      toast.error(msg);
    }
  };

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  const dirty = Object.keys(draft).length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <div>
            <CardTitle>Settings</CardTitle>
            <p className="text-xs text-muted mt-1">
              Re-reads the settings table — use after scripts write settings directly.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && <Badge tone="warn">{Object.keys(draft).length} unsaved</Badge>}
            <Button variant="ghost" size="sm" disabled={reloading} onClick={onReload}>
              <RefreshCw className="w-3.5 h-3.5" /> Reload from DB
            </Button>
            <Button variant="primary" size="sm" disabled={saving || !dirty} onClick={onSave}>
              <Save className="w-3.5 h-3.5" /> Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="general">
            <TabsList>
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="risk">Risk caps</TabsTrigger>
              <TabsTrigger value="whitelist">Whitelist</TabsTrigger>
              <TabsTrigger value="v5">V5 modules</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-3 pt-2">
              <Field label="Dry run">{boolField("dry_run", true)}</Field>
              <Field label="Approval mode">
                {selectField("approval_mode", ["manual", "auto"], "manual")}
              </Field>
              <Field label="Paused">{boolField("paused", false)}</Field>
              <Field label="Schedule cron — minute">
                <Input
                  value={get<string>("schedule_cron_minute", "5")}
                  onChange={(e) => setField("schedule_cron_minute", e.target.value)}
                />
              </Field>
              <Field label="Schedule cron — hour">
                <Input
                  value={get<string>("schedule_cron_hour", "*")}
                  onChange={(e) => setField("schedule_cron_hour", e.target.value)}
                />
              </Field>
              <Field label="Let users trigger cycles">
                {boolField("users_can_run_cycle", false)}
              </Field>
              <Alert tone="info">
                When ON, regular users see a "Run cycle" button in their
                topbar and the bot also fires a fresh cycle the instant
                they paste their Binance keys. Triggers go through{" "}
                <code>RUNNER.run_cycle_async</code>; the cycle is global,
                fan-out then writes each user's <code>user_cycles</code> row.
              </Alert>
            </TabsContent>

            <TabsContent value="risk" className="space-y-3 pt-2">
              <Field label="Restrict SELL to bot-acquired only">
                {boolField("restrict_sells_to_bot_acquired", false)}
              </Field>
              <Alert tone="info">
                When ON, the risk guard rejects any SELL where the bot's
                net acquired qty (sum of BUY filled_qty − SELL filled_qty
                across the trades table, real trades only) is{" "}
                <code>≤ 0</code>. Protects pre-existing user-held balances
                from being sold. The bot can still freely round-trip
                positions it bought itself.
              </Alert>
              <Field label="Ignore non-bot holdings in cycle snapshot">
                {boolField("cycle_ignore_non_bot_holdings", true)}
              </Field>
              <Alert tone="info">
                Snapshot-side counterpart of the above. When ON, base
                balances visible to the LLM are clipped to{" "}
                <code>min(actual_free, bot_acquired_qty)</code>, so manually
                held assets don't appear in the prompt's holdings or
                <code> sellable_symbols</code>. Turn OFF if you want the
                LLM to be able to recommend SELLing pre-existing holdings
                (also requires the restriction above to be OFF).
              </Alert>
              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="font-medium">Wallet-level (fallback)</div>
                <Field label="Max total exposure (USD)">{numberField("max_total_exposure_usd", 50, 1)}</Field>
                <Field label="Max per trade (USD)">{numberField("max_per_trade_usd", 10, 0.5)}</Field>
                <Field label="Min per trade (USD)">{numberField("min_per_trade_usd", 5, 0.5)}</Field>
                <p className="text-xs text-muted">
                  Used by any code path that doesn't pass a source. Per-source
                  values below override these for the hourly cycle.
                </p>
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="font-medium">Hourly bot caps</div>
                <Field label="Max total exposure (USD)">
                  {numberField("hourly_max_total_exposure_usd", 1500, 10)}
                </Field>
                <Field label="Max per trade (USD)">
                  {numberField("hourly_max_per_trade_usd", 20, 0.5)}
                </Field>
                <Field label="Max trades / day">{intField("max_trades_per_day", 8)}</Field>
                <Field label="Cooldown between trades (min)">
                  {intField("cooldown_minutes_between_trades", 30)}
                </Field>
                <p className="text-xs text-muted">
                  Hourly bot uses these. Scope: open hourly_positions only.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="whitelist" className="space-y-3 pt-2">
              <Field label="Symbols (comma-separated BASE/QUOTE)">
                <Input
                  value={
                    Array.isArray(get<string[]>("symbol_whitelist", []))
                      ? (get<string[]>("symbol_whitelist", []) as string[]).join(", ")
                      : ""
                  }
                  onChange={(e) =>
                    setField(
                      "symbol_whitelist",
                      e.target.value
                        .split(",")
                        .map((x) => x.trim().toUpperCase())
                        .filter(Boolean),
                    )
                  }
                  placeholder="BTC/USDT, ETH/USDT, SOL/USDT"
                />
              </Field>
              <Field label="Quote asset">
                <Input
                  value={get<string>("quote_asset", "USDT")}
                  onChange={(e) => setField("quote_asset", e.target.value.toUpperCase())}
                />
              </Field>
              <div className="border-t border-border pt-3 mt-3">
                <Field label="Scalp permanent deny list (comma-separated BASE/QUOTE)">
                  <Input
                    value={
                      Array.isArray(get<string[]>("scalp_symbol_blocklist", []))
                        ? (get<string[]>("scalp_symbol_blocklist", []) as string[]).join(", ")
                        : ""
                    }
                    onChange={(e) =>
                      setField(
                        "scalp_symbol_blocklist",
                        e.target.value
                          .split(",")
                          .map((x) => x.trim().toUpperCase())
                          .filter(Boolean),
                      )
                    }
                    placeholder="RUNE/USDT, WLD/USDT"
                  />
                </Field>
                <p className="text-xs text-muted">
                  Symbols listed here are NEVER added to the scalp whitelist —
                  not by the auto-tuner, not by an audit-applied patch, not by
                  a manual write. Adding a symbol here also strips it from the
                  current scalp whitelist on save. Use for chronic losers /
                  symbols you've decided are off-limits for the scalp strategy.
                  Doesn't affect the hourly bot.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="v5" className="space-y-4 pt-2">
              <Alert tone="warn">
                V5 subsystems all default OFF. None of them place live trades —
                MPT writes target weights only; sentiment/net-flow biases the
                snapshot prioritizer.
              </Alert>

              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="font-medium">Portfolio (MPT)</div>
                <Field label="Enabled">{boolField("mpt_enabled", false)}</Field>
                <Field label="Auto-rebalance (places real orders)">
                  {boolField("mpt_auto_rebalance_enabled", false)}
                </Field>
                <Field label="Objective">
                  {selectField("mpt_objective", ["max_sharpe","min_volatility","hrp"], "max_sharpe")}
                </Field>
                <Field label="Exponentially-weighted covariance">
                  {boolField("mpt_use_exp_cov", false)}
                </Field>
                <Field label="Use sentiment views (Black-Litterman)">
                  {boolField("mpt_use_sentiment_views", false)}
                </Field>
                <Field label="Sentiment view strength">
                  {numberField("mpt_view_strength", 0.05, 0.01)}
                </Field>
                <Field label="Rebalance check (h)">
                  {intField("mpt_rebalance_check_interval_hours", 24)}
                </Field>
                <Field label="Rebalance threshold %">
                  {numberField("mpt_rebalance_threshold_pct", 0.05, 0.01)}
                </Field>
                <Field label="Max per-asset weight">
                  {numberField("mpt_max_per_asset_weight", 0.4, 0.05)}
                </Field>
                <Field label="Min weight cutoff">
                  {numberField("mpt_min_weight_cutoff", 0.01, 0.005)}
                </Field>
                <Field label="L2 gamma">{numberField("mpt_l2_gamma", 0.1, 0.05)}</Field>
                <Field label="Max trades / rebalance">
                  {intField("mpt_max_rebalance_trades_per_run", 2)}
                </Field>
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="font-medium">Net-flows (Etherscan)</div>
                <Field label="Enabled">{boolField("intelligence_netflow_enabled", false)}</Field>
                <Field label="Cache / refresh (min)">
                  {intField("intelligence_netflow_cache_minutes", 60)}
                </Field>
                <Alert tone="info">
                  Requires <code>ETHERSCAN_API_KEY</code> in <code>.env</code>. Without it, the
                  tracker is fail-closed and writes nothing.
                </Alert>
              </div>

              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="font-medium">Sentiment (RSS + Gemini)</div>
                <Field label="Enabled">{boolField("intelligence_sentiment_enabled", false)}</Field>
                <Field label="Interval (min)">
                  {intField("intelligence_sentiment_interval_minutes", 30)}
                </Field>
                <Field label="Aggregation window (h)">
                  {intField("intelligence_sentiment_window_hours", 24)}
                </Field>
                <Field label="RSS feeds (one URL per line)">
                  <textarea
                    className="min-h-[88px] w-full rounded-md border border-border bg-bg px-3 py-2 text-sm font-mono"
                    value={
                      Array.isArray(get<string[]>("intelligence_sentiment_feeds", []))
                        ? (get<string[]>("intelligence_sentiment_feeds", []) as string[]).join("\n")
                        : ""
                    }
                    onChange={(e) =>
                      setField(
                        "intelligence_sentiment_feeds",
                        e.target.value
                          .split("\n")
                          .map((x) => x.trim())
                          .filter(Boolean),
                      )
                    }
                  />
                </Field>
              </div>

            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] items-center gap-2">
      <Label>{label}</Label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
